/** @jsxImportSource @emotion/react */
import {
  Button,
  Form,
  RadioGroup,
  Stack,
  Text,
  useBasisForm,
  useFieldArray,
  useTheme,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { Layout } from "components/Layout/Layout";
import { yesNoOptions } from "library/sharedOptions";
import { getNumberOfSteps } from "pages/HardshipApplication/reducer/hardshipSelectors";
import { usePageTracking } from "react-event-tracker";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import { ActionTypes, LiabilitiesState } from "../reducer/types";
import { LiabilityBox } from "./LiabilityBox";

export const Liabilities = () => {
  const { state, dispatch } = useHardshipApplicationContext();
  const { liabilities } = state;
  const theme = useTheme();

  const { methods, Field } = useBasisForm<LiabilitiesState>({
    defaultValues: liabilities,
  });

  const { navigate, goBack } = useNav();

  const { control, reset } = methods;
  const { fields, remove, append } = useFieldArray({
    control,
    name: "liabilities",
  });

  usePageTracking({
    page: {
      pageType: "input-page",
      siteSection: "hardship",
      siteSubSection: "liabilities",
      pageTitle: "What do you owe?",
    },
    user: {
      authenticationStatus: "not logged in",
    },
  });

  const onSubmit = (values: LiabilitiesState) => {
    dispatch({ type: ActionTypes.COMPLETE_LIABILITIES, ...values });
    navigate("your-income");
  };

  const addLiability = () =>
    append({
      liabilityType: "",
      balanceOwing: "",
      financialInstitution: "",
      minimumRepayment: {
        amount: "",
        frequency: "monthly",
      },
    });

  const onHadOtherMortgagesChange: React.ChangeEventHandler<HTMLInputElement> =
    (e) => {
      const value = e.target.value as LiabilitiesState["hadOtherMortgages"];

      if (value === "yes") {
        addLiability();
      } else if (value === "no") {
        reset({ hadOtherMortgages: value });
      }
    };

  const numberOfSteps = getNumberOfSteps(state);

  return (
    <Layout>
      <Layout.Header>Hardship Care application</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8">
            <Stack gap="4">
              <Text>
                <strong>Step 4 of {numberOfSteps}</strong>
              </Text>
              <Text as="h2" textStyle="heading4">
                What do you owe?
              </Text>
            </Stack>
            <Stack width="100%" width-sm="600" gap="8">
              <Field
                name="hadOtherMortgages"
                label="Do you have any other mortgages, personal loans or credit cards with any other financial institution(s)?"
                options={yesNoOptions}
                columns={2}
                onChange={onHadOtherMortgagesChange}
                as={RadioGroup}
              />
              {fields.map((value, index) => (
                <LiabilityBox
                  key={value.id}
                  value={value}
                  index={index}
                  onRemove={() => remove(index)}
                  control={control}
                />
              ))}
            </Stack>
            {fields.length > 0 && fields.length < 10 && (
              <button
                type="button"
                css={{
                  appearance: "none",
                  border: "none",
                  color: theme.colors.highlight.blue.t100,
                  ...theme.getTextStyleCSS("subtitle2"),
                }}
                onClick={addLiability}
              >
                <strong>
                  <span aria-hidden>+ </span>Add another liability
                </strong>
              </button>
            )}
            <FormActions>
              <FormActions.Primary width="172">
                <Button variant="primary" type="submit">
                  Next
                </Button>
              </FormActions.Primary>
              <FormActions.Secondary width="172">
                <Button variant="secondary" onClick={goBack}>
                  Back
                </Button>
              </FormActions.Secondary>
            </FormActions>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
